import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { VoiceProfile, logger } from '@musedrops/shared';
import { voiceProfileApi } from '../../../services/voiceProfileApi';
import { ModelService } from '../../../services/modelService';
import { SectionSaveButtons } from '../../settings/components/SectionSaveButtons';

interface VoiceModelPreset {
  key: string;
  name: string;
  type: string;
  voiceSet?: string | null;
}

/** One grid column: a voice set and the TTS presets that resolve through it. */
interface VoiceSetColumn {
  key: string;
  presetNames: string[];
}

interface VoiceProfilesSettingsState {
  voiceProfiles: VoiceProfile[];
  voiceSets: VoiceSetColumn[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  hasChanges: boolean;
  voiceMappings: Record<string, Record<string, string>>; // profileId -> voiceSet -> voiceName
}

/**
 * Voice sets are the keys of voice_profiles.model_voices: a preset's voiceSet,
 * or its own key when it has none. Keys already present on a profile but no
 * longer used by any preset stay visible so stale mappings can be cleaned up.
 */
function deriveVoiceSets(
  presets: VoiceModelPreset[],
  profiles: VoiceProfile[]
): VoiceSetColumn[] {
  const columns = new Map<string, VoiceSetColumn>();
  presets.forEach(preset => {
    const key = preset.voiceSet || preset.key;
    const column = columns.get(key) ?? { key, presetNames: [] };
    column.presetNames.push(preset.name);
    columns.set(key, column);
  });
  profiles.forEach(profile => {
    Object.keys(profile.modelVoices || {}).forEach(key => {
      if (!columns.has(key)) columns.set(key, { key, presetNames: [] });
    });
  });
  return Array.from(columns.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export const VoiceProfilesSettings: React.FC = () => {
  const notify = useNotify();
  const [state, setState] = useState<VoiceProfilesSettingsState>({
    voiceProfiles: [],
    voiceSets: [],
    loading: true,
    saving: false,
    error: null,
    hasChanges: false,
    voiceMappings: {},
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const [profiles, presets] = await Promise.all([
        voiceProfileApi.getVoiceProfiles(),
        ModelService.getAvailableModels(),
      ]);

      // Filter to only voice/TTS model presets
      const ttsPresets = presets.filter(preset => preset.type === 'voice');

      // Initialize voice mappings from existing profile data
      const mappings: Record<string, Record<string, string>> = {};
      profiles.forEach(profile => {
        mappings[profile.id] = profile.modelVoices;
      });

      setState(prev => ({
        ...prev,
        voiceProfiles: profiles,
        voiceSets: deriveVoiceSets(ttsPresets, profiles),
        voiceMappings: mappings,
        loading: false,
        hasChanges: false,
      }));
    } catch (error) {
      logger.error('Failed to load voice profiles data:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load data',
        loading: false,
      }));
    }
  };

  const handleVoiceMappingChange = (
    profileId: string,
    voiceSet: string,
    voiceName: string
  ) => {
    setState(prev => ({
      ...prev,
      voiceMappings: {
        ...prev.voiceMappings,
        [profileId]: {
          ...prev.voiceMappings[profileId],
          [voiceSet]: voiceName,
        },
      },
      hasChanges: true,
    }));
  };

  const handleSave = async () => {
    try {
      setState(prev => ({ ...prev, saving: true, error: null }));

      // Prepare updates for all profiles
      const updates = state.voiceProfiles.map(profile => ({
        id: profile.id,
        modelVoices: state.voiceMappings[profile.id] || {},
      }));

      await voiceProfileApi.bulkUpdateVoiceProfiles(updates);

      setState(prev => ({
        ...prev,
        saving: false,
        hasChanges: false,
      }));

      // Show success toast
      notify('Voice profile mappings saved successfully!', { type: 'success' });
    } catch (error) {
      logger.error('Failed to save voice profiles:', error);
      setState(prev => ({
        ...prev,
        saving: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to save voice profiles',
      }));
    }
  };

  const handleDiscard = () => {
    // Reset mappings to original profile data
    const mappings: Record<string, Record<string, string>> = {};
    state.voiceProfiles.forEach(profile => {
      mappings[profile.id] = profile.modelVoices;
    });

    setState(prev => ({
      ...prev,
      voiceMappings: mappings,
      hasChanges: false,
      error: null,
    }));
  };

  if (state.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      {state.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {state.error}
        </Alert>
      )}

      {state.voiceSets.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No TTS model presets found. Please configure TTS model presets first.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Columns are voice sets. Each TTS preset resolves voices through its
            voice set (or its own key when it has none), so presets sharing a
            voice library share one mapping.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200 }}>Voice Profile</TableCell>
                  {state.voiceSets.map(voiceSet => (
                    <TableCell key={voiceSet.key} sx={{ minWidth: 150 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {voiceSet.key}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {voiceSet.presetNames.length > 0
                          ? voiceSet.presetNames.join(', ')
                          : 'No preset uses this set'}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.voiceProfiles.map(profile => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {profile.key}
                        </Typography>
                      </Box>
                    </TableCell>
                    {state.voiceSets.map(voiceSet => (
                      <TableCell key={voiceSet.key}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Voice name"
                          value={
                            state.voiceMappings[profile.id]?.[voiceSet.key] ||
                            ''
                          }
                          onChange={e =>
                            handleVoiceMappingChange(
                              profile.id,
                              voiceSet.key,
                              e.target.value
                            )
                          }
                          disabled={state.saving}
                          fullWidth
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <SectionSaveButtons
            hasChanges={state.hasChanges}
            saving={state.saving}
            onSave={handleSave}
            onDiscard={handleDiscard}
            disabled={state.voiceProfiles.length === 0}
          />
        </>
      )}
    </Box>
  );
};
